import * as Api from '@/api';
import {
  Badge,
  BasePacket,
  BasePacketKind,
  Outcome,
  Objective
} from '@/models/packet';
import { defineModule, localActionContext } from 'direct-vuex';
import * as Utils from '@/utils';
import Icons, { IconData } from '@/icons';
import { WebRequestStatus } from '@/models';

interface State {
  packet: BasePacket;
  badges: Badge[];

  // These statuses should stop at success/failure,
  // and not cycle back to ready
  packetCreateStatus: WebRequestStatus;
  badgesCreateStatus: WebRequestStatus;
}

const fillOutObjective = (
  fields: Omit<Objective, 'objectiveId'>
): Objective => {
  return {
    ...fields,
    objectiveId: Utils.idGenerator.next()
  };
};

const buildEmptyPacket = (kind: BasePacketKind): BasePacket => {
  return {
    kind: kind,
    packetId: Utils.idGenerator.next(),
    name: '',
    description: '',
    objectives:
      kind === BasePacketKind.MULTI_POINT
        ? []
        : [
            fillOutObjective({
              name: '',
              description: '',
              iconData: Icons.defaultIcon,
              useOutcomeIcon: true,
              isActive: true
            })
          ],
    outcomes: [],
    external: false,
    public: false,
    canEdit: true,
    usage: 0
  };
};

const getInitialState = (kind: BasePacketKind): State => {
  return {
    packet: buildEmptyPacket(kind),
    badges: [],
    packetCreateStatus: WebRequestStatus.READY,
    badgesCreateStatus: WebRequestStatus.READY
  };
};

const buildStore = (kind: BasePacketKind) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const packetCreatorActionContext = (context: any) =>
    // eslint-disable-next-line  @typescript-eslint/no-use-before-define
    localActionContext(context, packetCreator);

  const packetCreator = defineModule({
    namespaced: true as true,
    state: getInitialState(kind),
    getters: {
      messages: (state): string[] => {
        const messages = [];

        if (!state.packet.name && !state.packet.description) {
          messages.push('Please add a name and description for these cogs');
        } else if (!state.packet.name) {
          messages.push('Please name these cogs');
        } else if (!state.packet.description) {
          messages.push('Please describe these cogs');
        }

        if (!state.badges.length) {
          if (state.packet.kind === BasePacketKind.SINGLE_POINT) {
            messages.push('Please add at least one cog');
          } else {
            messages.push('Please add at least one row and one column');
          }
        }

        return messages;
      }
    },
    mutations: {
      setPacket: (state, packet: BasePacket) => {
        state.packet = packet;
      },

      setBadges: (state, badges: Badge[]) => {
        state.badges = badges;
      },

      updateName: (state, name: string) => {
        state.packet = {
          ...state.packet,
          name
        };
      },

      updateDescription: (state, description: string) => {
        state.packet = {
          ...state.packet,
          description
        };
      },

      addBadge: (state, badge: Badge) => {
        state.badges = [...state.badges, badge];
      },

      updateBadge: (state, badge: Badge) => {
        state.badges = Utils.updateFirstWhere(
          state.badges,
          b => b.badgeId === badge.badgeId,
          () => badge
        );
      },

      removeBadgesForOutcome: (state, outcomeId: number) => {
        state.badges = state.badges.filter(
          badge => badge.outcomeId !== outcomeId
        );
      },

      removeBadgesForObjective: (state, objectiveId: number) => {
        state.badges = state.badges.filter(
          badge => badge.objectiveId !== objectiveId
        );
      },

      addOutcome: (state, outcome: Outcome) => {
        const insertIndex = outcome.sortOrder;

        const head = state.packet.outcomes.slice(0, insertIndex);
        const tail = state.packet.outcomes.slice(insertIndex);

        const inserted = [...head, outcome, ...tail];

        const reindexed = inserted.map((o, index) => ({
          ...o,
          sortOrder: index
        }));

        state.packet = {
          ...state.packet,
          outcomes: reindexed
        };
      },

      setOutcomeOrder: (state, outcomes: Outcome[]) => {
        state.packet = {
          ...state.packet,
          outcomes: outcomes.map((o, index) => ({
            ...o,
            sortOrder: index
          }))
        };
      },

      deleteOutcome: (state, { outcomeId }: Outcome) => {
        state.packet = {
          ...state.packet,
          outcomes: state.packet.outcomes.filter(o => o.outcomeId !== outcomeId)
        };
      },

      updateOutcome: (state, outcome: Outcome) => {
        state.packet = {
          ...state.packet,
          outcomes: Utils.updateFirstWhere(
            state.packet.outcomes,
            o => o.outcomeId === outcome.outcomeId,
            () => outcome
          )
        };
      },

      addObjective: (state, objective: Objective) => {
        state.packet = {
          ...state.packet,
          objectives: state.packet.objectives.concat([objective])
        };
      },

      deleteObjective: (state, { objectiveId }: Objective) => {
        state.packet = {
          ...state.packet,
          objectives: state.packet.objectives.filter(
            o => o.objectiveId !== objectiveId
          )
        };
      },

      updateObjective: (state, objective: Objective) => {
        state.packet = {
          ...state.packet,
          objectives: Utils.updateFirstWhere(
            state.packet.objectives,
            o => o.objectiveId === objective.objectiveId,
            () => objective
          )
        };
      },

      updatePacketCreateStatus: (state, status: WebRequestStatus) => {
        state.packetCreateStatus = status;
      },

      updateBadgesCreateStatus: (state, status: WebRequestStatus) => {
        state.badgesCreateStatus = status;
      },

      reset: state => {
        state.packet = buildEmptyPacket(state.packet.kind);
        state.badges = [];
        state.packetCreateStatus = WebRequestStatus.READY;
        state.badgesCreateStatus = WebRequestStatus.READY;
      }
    },
    actions: {
      createSinglePointGoal(
        context,
        payload: {
          name: string;
          color: string;
          iconData: IconData;
          description: string;
        }
      ) {
        const { state, dispatch } = packetCreatorActionContext(context);

        dispatch.insertOutcome({
          index: state.packet.outcomes.length,
          value: payload.name,
          color: payload.color,
          iconData: payload.iconData,
          badgeDescription: payload.description
        });
      },

      createMultiPointIndicator(
        context,
        payload: {
          index: number;
          value: string;
          color: string;
        }
      ) {
        const { dispatch } = packetCreatorActionContext(context);

        dispatch.insertOutcome({
          ...payload,
          iconData: Icons.defaultIcon
        });
      },

      insertOutcome(
        context,
        payload: {
          index: number;
          value: string;
          color: string;
          iconData: IconData;
          badgeDescription?: string;
        }
      ) {
        const { commit, state } = packetCreatorActionContext(context);

        const outcome = {
          value: payload.value,
          isActive: true,
          color: payload.color,
          sortOrder: payload.index,
          iconData: payload.iconData,
          outcomeId: Utils.idGenerator.next()
        };

        commit.addOutcome(outcome);

        state.packet.objectives.forEach(obj => {
          const description =
            payload.badgeDescription || `${obj.name} + ${outcome.value} = ?`;

          const badge = {
            badgeId: Utils.idGenerator.next(),
            outcomeId: outcome.outcomeId,
            objectiveId: obj.objectiveId,
            description
          };

          commit.addBadge(badge);
        });
      },

      updateOutcome(context, outcome: Outcome) {
        packetCreatorActionContext(context).commit.updateOutcome(outcome);
      },

      // The new packet's outcomes carry their sortOrder when it's created
      reorderOutcomes(context, outcomes: Outcome[]) {
        packetCreatorActionContext(context).commit.setOutcomeOrder(outcomes);
      },

      deleteOutcome(context, outcome: Outcome) {
        const { commit } = packetCreatorActionContext(context);

        commit.deleteOutcome(outcome);
        commit.removeBadgesForOutcome(outcome.outcomeId);
      },

      createObjective(
        context,
        payload: { name: string; description: string; iconData: IconData }
      ) {
        const { commit, state } = packetCreatorActionContext(context);

        const objective = {
          ...payload,
          objectiveId: Utils.idGenerator.next(),
          isActive: true,
          useOutcomeIcon: false
        };

        commit.addObjective(objective);

        state.packet.outcomes.forEach(outcome => {
          const description = `${objective.name} + ${outcome.value} = ?`;

          const badge = {
            badgeId: Utils.idGenerator.next(),
            outcomeId: outcome.outcomeId,
            objectiveId: objective.objectiveId,
            description
          };

          commit.addBadge(badge);
        });
      },

      updateObjective(context, objective: Objective) {
        const { commit } = packetCreatorActionContext(context);

        commit.updateObjective(objective);
      },

      deleteObjective(context, objective: Objective) {
        const { commit } = packetCreatorActionContext(context);

        commit.deleteObjective(objective);
        commit.removeBadgesForObjective(objective.objectiveId);
      },

      updateBadge(context, badge: Badge) {
        packetCreatorActionContext(context).commit.updateBadge(badge);
      },

      updateSinglePointBasicInfo(context, objective: Objective) {
        const { commit } = packetCreatorActionContext(context);

        commit.updateObjective(objective);
        commit.updateName(objective.name);
        commit.updateDescription(objective.description);
      },

      updateMultiPointBasicInfo(context, packet: BasePacket) {
        const { commit } = packetCreatorActionContext(context);

        commit.updateName(packet.name);
        commit.updateDescription(packet.description);
      },

      async createPacket(context, organizationId: number) {
        const { commit, state } = packetCreatorActionContext(context);

        await Api.secure.createPacketWithBadges(
          organizationId,
          state.packet,
          state.badges,
          packet => {
            commit.setPacket(packet);
            context.dispatch('org/addPacket', packet, { root: true });
          },
          commit.setBadges,
          commit.updatePacketCreateStatus,
          commit.updateBadgesCreateStatus
        );
      },

      reset(context) {
        const { commit } = packetCreatorActionContext(context);

        commit.reset();
      }
    }
  });

  return packetCreator;
};

export const singlePoint = buildStore(BasePacketKind.SINGLE_POINT);
export const multiPoint = buildStore(BasePacketKind.MULTI_POINT);
